import { useCallback, useEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { BUTTON_LABELS } from '../../constants/buttonLabels';
import { useCloseAllPositionsMutation } from '../../hooks/useCloseAllPositionsMutation';
import { useModalState } from '../../hooks/useModalState';
import { positionsQuery, usePositions } from '../../hooks/usePositions';
import { useProducts } from '../../hooks/useProducts';
import { useActiveSelectedSubaccount } from '../../hooks/useSelectedSubaccount';
import { isUserRejectedError } from '../../lib/errors';
import { localStorageKeys } from '../../lib/localstorageKeys';
import { invalidateQueries } from '../../lib/reactQueryHelpers';
import { showToast } from '../../lib/showToast';
import { closePositionsToast } from '../../lib/toasts';
import { queryClient } from '../../Web3Provider';
import { Alert } from '../Alert';
import { Button } from '../catalyst-ui-kit/button';
import { Checkbox, CheckboxField } from '../catalyst-ui-kit/checkbox';
import { Label } from '../catalyst-ui-kit/fieldset';
import { ResponsiveModal } from '../ResponsiveModal/ResponsiveModal';

export type CloseAllPositionsModalUiProps = {
  isOpen: boolean;
  onClose: () => void;
  hasPositions: boolean;
  isPending: boolean;
  failedPositionIds: string[];
  productTickers: Record<string, string>;
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  handleCloseAll: () => Promise<void>;
};

export const CloseAllPositionsModalUi: React.FC<CloseAllPositionsModalUiProps> = ({
  isOpen,
  onClose,
  hasPositions,
  isPending,
  failedPositionIds,
  productTickers,
  showModal,
  setShowModal,
  handleCloseAll,
}) => {
  return (
    <ResponsiveModal onClose={onClose} open={isOpen} title="Close All Positions">
      <div className="flex flex-col gap-6">
        <p className="text-xs font-medium text-contentSecondary">
          This action will close all your positions and cancel all associated TP/SL orders.
        </p>

        {failedPositionIds.length > 0 && (
          <Alert variant="error">
            <p>Failed to close the following positions:</p>
            <ul className="mt-1 list-inside list-disc">
              {failedPositionIds.map((id) => (
                <li key={id}>{productTickers[id] || `Unknown (${id})`}</li>
              ))}
            </ul>
          </Alert>
        )}

        <CheckboxField>
          <Checkbox
            id="disable-modal-checkbox"
            color="gray"
            checked={!showModal}
            onChange={(value) => setShowModal(!value)}
            className="cursor-pointer"
          />
          <Label
            htmlFor="disable-modal-checkbox"
            className="cursor-pointer text-3xs font-normal text-contentSecondary"
          >
            Do not show this confirmation modal anymore
          </Label>
        </CheckboxField>

        <div className="grid grid-cols-2 gap-2">
          <Button color="tertiary" onClick={onClose} disabled={isPending}>
            {BUTTON_LABELS.action.cancel}
          </Button>
          <Button color="blue" disabled={!hasPositions || isPending} onClick={handleCloseAll}>
            {failedPositionIds.length > 0
              ? BUTTON_LABELS.action.tryAgain
              : isPending
                ? BUTTON_LABELS.state.closing
                : BUTTON_LABELS.action.confirm}
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
};

export const CloseAllPositionsModal: React.FC = () => {
  const closeAllPositionsModal = useModalState((state) => state.closeAllPositionsModal);
  const setCloseAllPositionsModal = useModalState((state) => state.setCloseAllPositionsModal);
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { data: positions } = usePositions({ subaccountId: selectedSubaccount?.id });
  const { data: products = [] } = useProducts();
  const [failedPositionIds, setFailedPositionIds] = useState<string[]>([]);
  const [showModal, setShowModal] = useLocalStorage(
    localStorageKeys.showConfirmCloseAllModal,
    true
  );

  const [pendingShowModal, setPendingShowModal] = useState(showModal);

  const { mutateAsync: closeAllPositions, isPending } = useCloseAllPositionsMutation();

  const closeModal = useCallback(() => {
    setCloseAllPositionsModal('closed');
    setFailedPositionIds([]);
    setPendingShowModal(showModal);

    if (pendingShowModal !== showModal) {
      setShowModal(pendingShowModal);
    }
  }, [showModal, pendingShowModal, setCloseAllPositionsModal, setShowModal]);

  const handleCloseAll = useCallback(async () => {
    if (!positions || positions.length === 0) return;

    setFailedPositionIds([]);
    try {
      const { successfulPositionIds, failedPositionIds } = await closeAllPositions();

      showToast(closePositionsToast({ successfulPositionIds, failedPositionIds }));

      if (successfulPositionIds.length > 0) {
        invalidateQueries(
          queryClient,
          positionsQuery({ subaccountId: selectedSubaccount?.id }).queryKey
        );
      }
      if (failedPositionIds.length > 0) {
        setFailedPositionIds(failedPositionIds);
      } else {
        closeModal();
      }
    } catch (error) {
      if (!isUserRejectedError(error)) {
        console.error('Error closing all positions:', error);
        showToast(
          closePositionsToast({
            successfulPositionIds: [],
            failedPositionIds: positions.map(({ productId }) => productId),
          })
        );
      }
    }
  }, [positions, closeAllPositions, selectedSubaccount?.id, closeModal]);

  const productTickers = products.reduce<Record<string, string>>((acc, product) => {
    acc[product.id] = product.displayTicker;
    return acc;
  }, {});

  // If the user has disabled confirmation modals, call handleCloseAll without showing the modal
  useEffect(() => {
    if (closeAllPositionsModal === 'open' && !showModal) {
      handleCloseAll().catch(console.error).finally(closeModal);
    }
  }, [closeAllPositionsModal, showModal, closeModal, handleCloseAll, setCloseAllPositionsModal]);

  return (
    <CloseAllPositionsModalUi
      isOpen={closeAllPositionsModal === 'open' && showModal}
      onClose={closeModal}
      hasPositions={positions !== undefined && positions.length > 0}
      isPending={isPending}
      failedPositionIds={failedPositionIds}
      productTickers={productTickers}
      showModal={pendingShowModal}
      setShowModal={setPendingShowModal}
      handleCloseAll={handleCloseAll}
    />
  );
};
